import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { withRouter, Redirect } from 'react-router-dom';
import Cookies from 'js-cookie';
import classNames from 'classnames';
import { isEmpty } from 'lodash';

import config from '../../config';
import routeConfiguration from '../../routing/routeConfiguration';
import { camelize } from '../../util/string';
import { pathByRouteName } from '../../util/routes';
import { isReservedEmail } from '../../util/validators';
import { apiBaseUrl } from '../../util/api';
import { FormattedMessage, injectIntl, intlShape } from '../../util/reactIntl';
import { ALL_GENDER, CONFIRM_PASSWORD, PASSWORD, propTypes, TEXT } from '../../util/types';
import { ensureCurrentUser } from '../../util/data';
import {
  isSignupEmailTakenError,
  isTooManyEmailVerificationRequestsError,
} from '../../util/errors';

import {
  login,
  authenticationInProgress,
  signup,
  signupWithIdp,
  uploadImage,
} from '../../ducks/Auth.duck';
import { isScrollingDisabled } from '../../ducks/UI.duck';
import { sendVerificationEmail } from '../../ducks/user.duck';
import { manageDisableScrolling } from '../../ducks/UI.duck';

import {
  Page,
  NamedLink,
  NamedRedirect,
  LinkTabNavHorizontal,
  IconEmailSent,
  InlineTextButton,
  SocialLoginButton,
  IconClose,
  LayoutSingleColumn,
  LayoutWrapperTopbar,
  LayoutWrapperMain,
  LayoutWrapperFooter,
  Footer,
  Modal,
  IconCollection,
} from '../../components';

import TopbarContainer from '../../containers/TopbarContainer/TopbarContainer';

import ConfirmSignupForm from './ConfirmSignupForm/ConfirmSignupForm';
import LoginForm from './LoginForm/LoginForm';
import SignupForm from './SignupForm/SignupForm';

import {
  TOS_ASSET_NAME,
  TermsOfServiceContent,
} from '../../containers/TermsOfServicePage/TermsOfServicePage';

import css from './AuthenticationPage.module.css';
import { FacebookLogo, GoogleLogo } from './socialLoginLogos';
import { PrivacyPolicyContent } from '../PrivacyPolicyPage/PrivacyPolicyPage';
import { ASSET_NAME_2 } from './AuthenticationPage.duck';
const LOGIN_FORM_ICON = 'loginFormIcon';
const onImageUploadHandler = (values, fn) => {
  const { id, imageId, file } = values;
  if (file) {
    fn({ id, imageId, file });
  }
};
export class AuthenticationPageComponent extends Component {
  constructor(props) {
    super(props);
    this.state = {
      isValid: false,
      errorMessage: '',
      tosModalOpen: false,
      ppModalOpen: false,
      isPasswordVisible: false,
      isConfirmPasswordVisible: false,
      authError: Cookies.get('st-autherror')
        ? JSON.parse(Cookies.get('st-autherror').replace('j:', ''))
        : null,
      authInfo: Cookies.get('st-authinfo')
        ? JSON.parse(Cookies.get('st-authinfo').replace('j:', ''))
        : null,
    };
  }

  componentDidMount() {
    // Remove the autherror cookie once the content is saved to state
    // because we don't want to show the error message e.g. after page refresh
    Cookies.remove('st-autherror');
  }

  render() {
    const {
      authInProgress,
      currentUser,
      intl,
      isAuthenticated,
      location,
      loginError,
      scrollingDisabled,
      signupError,
      submitLogin,
      submitSignup,
      confirmError,
      submitSingupWithIdp,
      tab,
      sendVerificationEmailInProgress,
      sendVerificationEmailError,
      onResendVerificationEmail,
      onManageDisableScrolling,
      tosAssetsData,
      tosFetchInProgress,
      tosFetchError,
      onImageUpload,
      isUsernameExits,
      history,
    } = this.props;

    if (location?.state) {
      document.documentElement.style.setProperty('--marketplaceColor', '#478cbc');
      document.documentElement.style.setProperty('--marketplaceColorDark', '#3065a1');
      document.documentElement.style.setProperty('--matterColorNegative', '#f8f7f7');
      document.documentElement.style.setProperty('--marketplaceInitialDefaultColor', '#121313');
      document.documentElement.style.setProperty('--matterColorBright', '#F1F0F0');
    }
    const isConfirm = tab === 'confirm';
    const isLogin = tab === 'login';
    const locationFrom = location.state && location.state.from ? location.state.from : null;
    const authinfoFrom =
      this.state.authInfo && this.state.authInfo.from ? this.state.authInfo.from : null;
    const from = locationFrom ? locationFrom : authinfoFrom ? authinfoFrom : null;

    const user = ensureCurrentUser(currentUser);
    const currentUserLoaded = !!user.id;

    // We only want to show the email verification dialog in the signup
    // tab if the user isn't being redirected somewhere else
    // (i.e. `from` is present). We must also check the `emailVerified`
    // flag only when the current user is fully loaded.
    const showEmailVerification = !isLogin && currentUserLoaded && !user.attributes.emailVerified;

    // Already authenticated, redirect away from auth page
    if (isAuthenticated && from) {
      return <Redirect to={from} />;
    } else if (isAuthenticated && currentUserLoaded && !showEmailVerification) {
      return (
        <NamedRedirect
          name="LandingPageListingStore"
          params={{ type: 'all', gender: ALL_GENDER }}
        />
      );
    }

    const loginErrorMessage = (
      <div className={css.error}>
        <FormattedMessage id="AuthenticationPage.loginFailed" />
      </div>
    );

    const signupErrorMessage = (
      <div className={css.error}>
        {isSignupEmailTakenError(signupError) ? (
          <FormattedMessage id="AuthenticationPage.signupFailedEmailAlreadyTaken" />
        ) : (
          <FormattedMessage id="AuthenticationPage.signupFailed" />
        )}
      </div>
    );

    const confirmErrorMessage = confirmError ? (
      <div className={css.error}>
        {isSignupEmailTakenError(confirmError) ? (
          <FormattedMessage id="AuthenticationPage.signupFailedEmailAlreadyTaken" />
        ) : (
          <FormattedMessage id="AuthenticationPage.signupFailed" />
        )}
      </div>
    ) : null;

    // eslint-disable-next-line no-confusing-arrow
    const errorMessage = (error, message) => (error ? message : null);
    const loginOrSignupError = isLogin
      ? errorMessage(loginError, loginErrorMessage)
      : errorMessage(signupError, signupErrorMessage);

    const fromState = { state: from ? { from } : null };

    const tabs = [
      {
        text: (
          <h1 className={css.tab}>
            <FormattedMessage id="AuthenticationPage.signupLinkText" />
          </h1>
        ),
        selected: !isLogin,
        linkProps: {
          name: 'SignupPage',
          to: fromState,
        },
      },
      // {
      //   text: (
      //     <h1 className={css.tab}>
      //       <FormattedMessage id="AuthenticationPage.loginLinkText" />
      //     </h1>
      //   ),
      //   selected: isLogin,
      //   linkProps: {
      //     name: 'LoginPage',
      //     to: fromState,
      //   },
      // },
    ];

    const validateUsername = params => {
      // Regular expressions for email, phone number, and strings without '@'
      const emailRegex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i; // Email format
      const phoneRegex = /^\d{10}$/; // Phone number must be exactly 10 digits
      const noAtSymbolRegex = /^[^@]*$/; // Ensure the string does not contain '@'

      // Check if the username is in an email, phone number format, or contains '@'
      if (
        emailRegex.test(params.userName) ||
        phoneRegex.test(params.userName) ||
        !noAtSymbolRegex.test(params.userName)
      ) {
        this.setState({
          isValid: false,
          errorMessage: 'Username cannot be an email, phone number, or contain "@"',
        });
      } else if (params.userName.length > 20) {
        // Check if it's longer than 20 characters
        this.setState({
          isValid: false,
          errorMessage: 'Username cannot be longer than 20 characters',
        });
      } else if (isReservedEmail(params.email)) {
        // Defense in depth: the email field's own validator already blocks
        // this in the UI, but guard the actual submit path too.
        this.setState({
          isValid: false,
          errorMessage:
            'This email address cannot be used for registration. Please use a different email address.',
        });
      } else {
        // Valid username
        this.setState({
          isValid: true,
          errorMessage: '',
        });
        submitSignup(params);
      }
    };

    const handleSubmitSignup = values => {
      const { fname, lname, userName, ...rest } = values;
      const params = { firstName: fname.trim(), lastName: lname.trim(), userName, ...rest };
      validateUsername(params);
    };

    const handleSubmitConfirm = values => {
      const { idpToken, email, firstName, lastName, idpId } = this.state.authInfo;
      const { email: newEmail, firstName: newFirstName, lastName: newLastName, ...rest } = values;

      // Pass email, fistName or lastName to Flex API only if user has edited them
      // sand they can't be fetched directly from idp provider (e.g. Facebook)

      const authParams = {
        ...(newEmail !== email && { email: newEmail }),
        ...(newFirstName !== firstName && { firstName: newFirstName }),
        ...(newLastName !== lastName && { lastName: newLastName }),
      };

      // If the confirm form has any additional values, pass them forward as user's protected data
      const protectedData = !isEmpty(rest) ? { ...rest } : null;

      submitSingupWithIdp({
        idpToken,
        idpId,
        ...authParams,
        ...(!!protectedData && { protectedData }),
      });
    };

    const handlePasswordVisibilityToggle = () => {
      this.setState(prevState => ({ isPasswordVisible: !prevState.isPasswordVisible }));
    };

    const handleConfirmPasswordVisibilityToggle = () => {
      this.setState(prevState => ({
        isConfirmPasswordVisible: !prevState.isConfirmPasswordVisible,
      }));
    };

    const isPasswordVisibleFunction = (visible, field) =>
      visible ? TEXT : field === PASSWORD ? PASSWORD : TEXT;

    const isConfirmPasswordVisibleFunction = (visible, field) =>
      visible ? TEXT : field === CONFIRM_PASSWORD ? PASSWORD : TEXT;

    const getDefaultRoutes = () => {
      const routes = routeConfiguration();
      const baseUrl = apiBaseUrl();

      // Route where the user should be returned after authentication
      // This is used e.g. with EditListingPage and ListingPage
      const fromParam = from ? `from=${from}` : '';

      // Default route where user is returned after successfull authentication
      const defaultReturn = pathByRouteName(
        'LandingPageListingStore',
        routes,
        {},
        { type: 'all', gender: ALL_GENDER }
      );
      const defaultReturnParam = defaultReturn ? `&defaultReturn=${defaultReturn}` : '';

      // Route for confirming user data before creating a new user
      const defaultConfirm = pathByRouteName('ConfirmPage', routes);
      const defaultConfirmParam = defaultConfirm ? `&defaultConfirm=${defaultConfirm}` : '';

      return { baseUrl, fromParam, defaultReturnParam, defaultConfirmParam };
    };
    const authWithFacebook = () => {
      const defaultRoutes = getDefaultRoutes();
      const { baseUrl, fromParam, defaultReturnParam, defaultConfirmParam } = defaultRoutes;
      window.location.href = `${baseUrl}/api/auth/facebook?${fromParam}${defaultReturnParam}${defaultConfirmParam}`;
    };

    const authWithGoogle = () => {
      const defaultRoutes = getDefaultRoutes();
      const { baseUrl, fromParam, defaultReturnParam, defaultConfirmParam } = defaultRoutes;
      window.location.href = `${baseUrl}/api/auth/google?${fromParam}${defaultReturnParam}${defaultConfirmParam}`;
    };

    const idp = this.state.authInfo
      ? this.state.authInfo.idpId.replace(/^./, str => str.toUpperCase())
      : null;

    // Form for confirming information frm IdP (e.g. Facebook)
    // before new user is created to Flex
    const confirmForm = (
      <div className={css.content}>
        <h1 className={css.signupWithIdpTitle}>
          <FormattedMessage id="AuthenticationPage.confirmSignupWithIdpTitle" values={{ idp }} />
        </h1>

        <p className={css.confirmInfoText}>
          <FormattedMessage id="AuthenticationPage.confirmSignupInfoText" />
        </p>
        {confirmErrorMessage}
        <ConfirmSignupForm
          className={css.form}
          onSubmit={handleSubmitConfirm}
          inProgress={authInProgress}
          onOpenTermsOfService={() => this.setState({ tosModalOpen: true })}
          onOpenPrivacyPolicy={() => this.setState({ ppModalOpen: true })}
          authInfo={this.state.authInfo}
          idp={idp}
        />
      </div>
    );

    // Social login buttons
    const showFacebookLogin = !!process.env.REACT_APP_FACEBOOK_APP_ID;
    const showGoogleLogin = !!process.env.REACT_APP_GOOGLE_CLIENT_ID;
    const showSocialLogins = showFacebookLogin || showGoogleLogin;

    const facebookButtonText = isLogin ? (
      <FormattedMessage id="AuthenticationPage.loginWithFacebook" />
    ) : (
      <FormattedMessage id="AuthenticationPage.signupWithFacebook" />
    );

    const googleButtonText = isLogin ? (
      <FormattedMessage id="AuthenticationPage.loginWithGoogle" />
    ) : (
      <FormattedMessage id="AuthenticationPage.signupWithGoogle" />
    );
    const socialLoginButtonsMaybe = showSocialLogins ? (
      <div className={css.idpButtons}>
        <div className={css.socialButtonsOr}>
          <span className={css.socialButtonsOrText}>
            <FormattedMessage id="AuthenticationPage.or" />
          </span>
        </div>

        {showFacebookLogin ? (
          <div className={css.socialButtonWrapper}>
            <SocialLoginButton onClick={() => authWithFacebook()}>
              <span className={css.buttonIcon}>{FacebookLogo}</span>
              {facebookButtonText}
            </SocialLoginButton>
          </div>
        ) : null}

        {showGoogleLogin ? (
          <div className={css.socialButtonWrapper}>
            <SocialLoginButton onClick={() => authWithGoogle()}>
              <span className={css.buttonIcon}>{GoogleLogo}</span>
              {googleButtonText}
            </SocialLoginButton>
          </div>
        ) : null}
      </div>
    ) : null;

    // Tabs for SignupForm and LoginForm
    const authenticationForms = (
      <div className={css.content}>
        {isLogin ? (
          <>
            <IconCollection icon={LOGIN_FORM_ICON} className={css.loginFormLogo} />
            {loginOrSignupError}
            <LoginForm
              className={css.loginForm}
              onSubmit={submitLogin}
              inProgress={authInProgress}
              isPasswordVisibleFunction={isPasswordVisibleFunction}
              isPasswordVisible={this.state.isPasswordVisible}
              handlePasswordVisibilityToggle={handlePasswordVisibilityToggle}
            />
          </>
        ) : (
          <>
            <div className={css.signupFormHeader}>
              <div className={css.IconBackWrapper}>
                <NamedLink
                  className={css.backButtonLink}
                  name="LandingPageListingStore"
                  params={{ type: 'all', gender: ALL_GENDER }}
                >
                  <IconCollection icon="backIcon" />
                  <span className={css.backButtonText}>
                    <FormattedMessage id="AuthenticationPage.backButtonText" />
                  </span>
                </NamedLink>
              </div>
              <LinkTabNavHorizontal className={css.tabs} tabs={tabs} />
            </div>
            {loginOrSignupError}
            <SignupForm
              className={css.signupForm}
              errorMessage={this.state.errorMessage}
              currentUser={currentUser}
              isPasswordVisibleFunction={isPasswordVisibleFunction}
              isConfirmPasswordVisibleFunction={isConfirmPasswordVisibleFunction}
              isPasswordVisible={this.state.isPasswordVisible}
              isConfirmPasswordVisible={this.state.isConfirmPasswordVisible}
              handlePasswordVisibilityToggle={handlePasswordVisibilityToggle}
              handleConfirmPasswordVisibilityToggle={handleConfirmPasswordVisibilityToggle}
              onSubmit={handleSubmitSignup}
              inProgress={authInProgress}
              onOpenTermsOfService={() => this.setState({ tosModalOpen: true })}
              onOpenPrivacyPolicy={() => this.setState({ ppModalOpen: true })}
              onImageUpload={e => onImageUploadHandler(e, onImageUpload)}
              isUserNameAlreadyExit={isUsernameExits}
            />
          </>
        )}

        {socialLoginButtonsMaybe}
      </div>
    );

    const formContent = isConfirm ? confirmForm : authenticationForms;

    const name = user.attributes.profile.firstName;
    const email = <span className={css.email}>{user.attributes.email}</span>;

    const resendEmailLink = (
      <InlineTextButton rootClassName={css.modalHelperLink} onClick={onResendVerificationEmail}>
        <FormattedMessage id="AuthenticationPage.resendEmailLinkText" />
      </InlineTextButton>
    );
    const fixEmailLink = (
      <NamedLink className={css.modalHelperLink} name="ContactDetailsPage">
        <FormattedMessage id="AuthenticationPage.fixEmailLinkText" />
      </NamedLink>
    );

    const resendErrorTranslationId = isTooManyEmailVerificationRequestsError(
      sendVerificationEmailError
    )
      ? 'AuthenticationPage.resendFailedTooManyRequests'
      : 'AuthenticationPage.resendFailed';
    const resendErrorMessage = sendVerificationEmailError ? (
      <p className={css.error}>
        <FormattedMessage id={resendErrorTranslationId} />
      </p>
    ) : null;

    const emailVerificationContent = (
      <div className={css.content}>
        <NamedLink className={css.verifyClose} name="ProfileSettingsPage">
          <span className={css.closeText}>
            <FormattedMessage id="AuthenticationPage.verifyEmailClose" />
          </span>
          <IconClose rootClassName={css.closeIcon} />
        </NamedLink>
        <IconEmailSent className={css.modalIcon} />
        <h1 className={css.modalTitle}>
          <FormattedMessage id="AuthenticationPage.verifyEmailTitle" values={{ name }} />
        </h1>
        <p className={css.modalMessage}>
          <FormattedMessage id="AuthenticationPage.verifyEmailText" values={{ email }} />
        </p>
        {resendErrorMessage}

        <div className={css.bottomWrapper}>
          <p className={css.modalHelperText}>
            {sendVerificationEmailInProgress ? (
              <FormattedMessage id="AuthenticationPage.sendingEmail" />
            ) : (
              <FormattedMessage id="AuthenticationPage.resendEmail" values={{ resendEmailLink }} />
            )}
          </p>
          <p className={css.modalHelperText}>
            <FormattedMessage id="AuthenticationPage.fixEmail" values={{ fixEmailLink }} />
          </p>
        </div>
      </div>
    );

    const siteTitle = config.siteTitle;
    const schemaTitle = isLogin
      ? intl.formatMessage({ id: 'AuthenticationPage.schemaTitleLogin' }, { siteTitle })
      : intl.formatMessage({ id: 'AuthenticationPage.schemaTitleSignup' }, { siteTitle });

    const topbarClasses = classNames({
      [css.hideOnMobile]: showEmailVerification,
    });
    const localBookmarks =
      typeof window !== 'undefined' && JSON.parse(window.localStorage.getItem('localBookmarks'));
    return (
      <Page
        title={schemaTitle}
        scrollingDisabled={scrollingDisabled}
        schema={{
          '@context': 'http://schema.org',
          '@type': 'WebPage',
          name: schemaTitle,
        }}
      >
        <LayoutSingleColumn>
          <LayoutWrapperTopbar>
            <TopbarContainer className={topbarClasses} lb={localBookmarks} />
          </LayoutWrapperTopbar>
          <LayoutWrapperMain className={css.layoutWrapperMain}>
            <div className={css.root}>
              {showEmailVerification ? emailVerificationContent : formContent}
            </div>
            <Modal
              id="AuthenticationPage.tos"
              isOpen={this.state.tosModalOpen}
              onClose={() => this.setState({ tosModalOpen: false })}
              usePortal
              onManageDisableScrolling={onManageDisableScrolling}
            >
              <div className={css.termsWrapper}>
                <TermsOfServiceContent
                  inProgress={tosFetchInProgress}
                  error={tosFetchError}
                  data={tosAssetsData?.[camelize(TOS_ASSET_NAME)]?.data}
                  history={history}
                />
              </div>
            </Modal>
            <Modal
              id="AuthenticationPage.pp"
              isOpen={this.state.ppModalOpen}
              onClose={() => this.setState({ ppModalOpen: false })}
              usePortal
              onManageDisableScrolling={onManageDisableScrolling}
            >
              <div className={css.termsWrapper}>
                <PrivacyPolicyContent
                  inProgress={tosFetchInProgress}
                  error={tosFetchError}
                  data={tosAssetsData?.[camelize(ASSET_NAME_2)]?.data}
                  history={history}
                />
              </div>
            </Modal>
          </LayoutWrapperMain>
          <LayoutWrapperFooter>
            <Footer ColorVariant="white" />
          </LayoutWrapperFooter>
        </LayoutSingleColumn>
      </Page>
    );
  }
}

AuthenticationPageComponent.defaultProps = {
  currentUser: null,
  loginError: null,
  signupError: null,
  confirmError: null,
  tab: 'signup',
  sendVerificationEmailError: null,
  showSocialLoginsForTests: false,
  tosAssetsData: null,
  tosFetchInProgress: false,
  tosFetchError: null,
};

const { bool, func, object, oneOf, shape } = PropTypes;

AuthenticationPageComponent.propTypes = {
  authInProgress: bool.isRequired,
  currentUser: propTypes.currentUser,
  isAuthenticated: bool.isRequired,
  loginError: propTypes.error,
  scrollingDisabled: bool.isRequired,
  signupError: propTypes.error,
  confirmError: propTypes.error,

  submitLogin: func.isRequired,
  submitSignup: func.isRequired,
  tab: oneOf(['login', 'signup', 'confirm']),

  sendVerificationEmailInProgress: bool.isRequired,
  sendVerificationEmailError: propTypes.error,
  onResendVerificationEmail: func.isRequired,
  onManageDisableScrolling: func.isRequired,

  // to fetch terms-of-service page asset
  // which is shown in modal
  tosAssetsData: object,
  tosFetchInProgress: bool,
  tosFetchError: propTypes.error,

  // from withRouter
  location: shape({ state: object }).isRequired,

  // from injectIntl
  intl: intlShape.isRequired,
};

const mapStateToProps = state => {
  const { isAuthenticated, loginError, signupError, confirmError, isUsernameExits } = state.Auth;

  const { currentUser, sendVerificationEmailInProgress, sendVerificationEmailError } = state.user;
  const { pageAssetsData: tosAssetsData, inProgress: tosFetchInProgress, error: tosFetchError } =
    state.hostedAssets || {};
  return {
    isUsernameExits,
    authInProgress: authenticationInProgress(state),
    currentUser,
    isAuthenticated,
    loginError,
    scrollingDisabled: isScrollingDisabled(state),
    signupError,
    confirmError,
    sendVerificationEmailInProgress,
    sendVerificationEmailError,
    tosAssetsData,
    tosFetchInProgress,
    tosFetchError,
  };
};

const mapDispatchToProps = dispatch => ({
  submitLogin: ({ email, password }) => dispatch(login(email, password)),
  onImageUpload: data => dispatch(uploadImage(data)),
  submitSignup: params => dispatch(signup(params)),
  submitSingupWithIdp: params => dispatch(signupWithIdp(params)),
  onResendVerificationEmail: () => dispatch(sendVerificationEmail()),
  onManageDisableScrolling: (componentId, disableScrolling) =>
    dispatch(manageDisableScrolling(componentId, disableScrolling)),
});

// Note: it is important that the withRouter HOC is **outside** the
// connect HOC, otherwise React Router won't rerender any Route
// components since connect implements a shouldComponentUpdate
// lifecycle hook.
//
// See: https://github.com/ReactTraining/react-router/issues/4671
const AuthenticationPage = compose(
  withRouter,
  connect(mapStateToProps, mapDispatchToProps),
  injectIntl
)(AuthenticationPageComponent);

export default AuthenticationPage;
